import { useMemo } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useGetUser } from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { tracking } from '@sib/tracking';
import { FileItem, FileStatus } from '@sib/types';
import { useSorting } from '@spendbrain/common/hooks';
import { paths } from '@spendbrain/config/paths';
import { FileUploadStore } from '@spendbrain/stores/fileUploadStore';
import {
  useUploadToBlobStorage,
  useBatchUpload,
} from '@spendbrain/utils/blob-storage-upload';
import { DROPZONE_CONFIG, useOnDropHandler } from '@spendbrain/utils/files';

interface UseUploadingFilesListProps {
  fileUploadStore: FileUploadStore;
  closeFileManager?: () => void;
  dropzoneConfig?: Partial<DropzoneOptions>;
}

export const useUploadingFilesList = ({
  fileUploadStore,
  closeFileManager,
  dropzoneConfig,
}: UseUploadingFilesListProps) => {
  const { t } = useTranslation();
  const {
    files,
    addFiles,
    getFiles,
    onFilesUploaded,
    updateFile,
    selectAll,
    removeUploadedFiles,
    getAreFilesUploading,
    removeFile,
    registerUploadController,
    unregisterUploadController,
  } = fileUploadStore;

  const path = useLocation();
  const navigate = useNavigate();
  const isOnDocumentsRepostioryPage = useMemo(
    () => Boolean(matchPath(paths.documents.root, path.pathname)),
    [path.pathname]
  );

  const areFilesUploading = getAreFilesUploading();
  const {
    organization: { id: organizationId, name: organizationName },
  } = useUserSetupStore();
  const { data: userData } = useGetUser();
  const { sortedItems: sortedFiles, toggleSortOrder } = useSorting<FileItem>(
    files,
    fileItem => fileItem.file.name
  );
  const { onDrop } = useOnDropHandler({
    addFiles,
  });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...(dropzoneConfig || DROPZONE_CONFIG),
    onDrop,
  });

  const { open } = useNotification();

  const fileUploadCallbacks = useMemo(
    () => ({
      onProgress: (fileItem: FileItem, progress: number) =>
        updateFile({
          ...fileItem,
          status: FileStatus.Uploading,
          progress,
        }),
      onSuccess: (fileItem: FileItem) => {
        if (fileItem.status === FileStatus.Uploaded) return;

        tracking.track('Completed file upload', {
          fileName: fileItem.file.name,
          organizationId,
        });

        updateFile({ ...fileItem, status: FileStatus.Uploaded });
      },
      onError: (fileItem: FileItem, error?: string, errorDetails?: unknown) => {
        tracking.track('Failed file upload', {
          error,
          errorDetails: JSON.stringify(errorDetails),
          fileName: fileItem.file.name,
          organizationId,
          userOrganizations: JSON.stringify(userData?.organizations),
        });
        updateFile({
          ...fileItem,
          status: FileStatus.Error,
          errorMessage: error,
        });
      },
    }),
    [updateFile, organizationId, userData]
  );
  const { uploadFileToBlobStorage: uploadSingleFileToBlobStorage } =
    useUploadToBlobStorage(fileUploadCallbacks);

  const { generateBatchId, checkForUploadStatus } = useBatchUpload({
    getFiles: fileUploadStore.getFiles,
    ...fileUploadCallbacks,
  });
  const uploadFiles = async () => {
    closeFileManager?.();
    open?.({
      type: 'info',
      message: t('fileManager.startUploadMessage'),
      description: t('fileManager.startUploadDescription'),
    });
    const filesToUpload = files.filter(
      file => file.status !== FileStatus.Error
    );
    if (!filesToUpload.length) return;

    const batchUploadId = generateBatchId();

    filesToUpload.forEach(fileItem => {
      updateFile({
        ...fileItem,
        status: FileStatus.Uploading,
        progress: 0,
      });
    });

    const uploadAllFiles = filesToUpload.map(fileItem => {
      const controller = new AbortController();
      registerUploadController(fileItem.id, controller);

      return uploadSingleFileToBlobStorage(fileItem, {
        totalFiles: filesToUpload.length,
        batchId: batchUploadId,
        abortSignal: controller.signal,
      }).finally(() => unregisterUploadController(fileItem.id));
    });

    await Promise.all(uploadAllFiles);

    await checkForUploadStatus(batchUploadId);

    const currentFiles = getFiles();

    const uploadedCount = currentFiles.filter(
      file => file.status === FileStatus.Uploaded
    ).length;
    const failedFiles = currentFiles.filter(
      file => file.status === FileStatus.Error
    );
    const failedCount = failedFiles.length;

    if (failedCount > 0) {
      open?.({
        type: 'error',
        message: t('fileManager.uploadFailedMessage'),
        description:
          uploadedCount > 0
            ? t('fileManager.uploadFailedPartialDescription', {
                uploadedCount,
                failedCount,
              })
            : t('fileManager.uploadFailedAllFailedDescription', {
                failedCount,
              }),
      });
    } else if (uploadedCount > 0) {
      open?.({
        type: 'success',
        message: t('fileManager.uploadSuccess.message'),
        description: t('fileManager.uploadSuccess.description', {
          uploadedCount,
        }),
        ...(!isOnDocumentsRepostioryPage && {
          action: {
            label: t('fileManager.uploadSuccess.redirectToDocumentsRepository'),
            onClick: () => navigate(paths.documents.root),
            ActionButtonProps: {
              variant: 'ghost',
            },
          },
        }),
      });
      onFilesUploaded?.();
      closeFileManager?.();
    }

    removeUploadedFiles();
  };

  const hasErrors = files.some(file => file.status === FileStatus.Error);

  const anySelected = files.some(file => file.selected);

  const allSelected =
    files.length > 0 && files.every(fileItem => fileItem.selected);

  const toggleSelectAllFiles = () => {
    selectAll(!allSelected);
  };

  const areThereAnyFiles = files.length > 0;

  return {
    files,
    sortedFiles,
    isDragActive,
    hasErrors,
    allSelected,
    areThereAnyFiles,
    areFilesUploading,
    anySelected,
    organizationName,
    organizationId,
    toggleSelectAllFiles,
    uploadFiles,
    toggleSortOrder,
    getRootProps,
    getInputProps,
    updateFile,
    removeFile,
  };
};
